import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import "@/App.css";
import { SiteHeader } from "./components/SiteHeader";
import { SiteFooter } from "./components/SiteFooter";
import { AnalysingState } from "./components/AnalysingState";
import { CoverageAssessment } from "./components/CoverageAssessment";
import { Copilot } from "./components/Copilot";
import { RepServices } from "./components/RepServices";
import { FrameworkBand } from "./components/FrameworkBand";
import { CountersBand } from "./components/CountersBand";
import { Testimonials } from "./components/Testimonials";
import { ClosingCTA } from "./components/ClosingCTA";
import { computeCoverage, actionableIds } from "./lib/coverage";
import { CopilotAvatar } from "./components/CopilotAvatar";
import { fmtMoney } from "./lib/pricing";
import { Sparkles, X, MessageSquare, ShieldCheck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULT_ASSUMPTIONS = {
  eu_residents: true, uk_residents: true, swiss_residents: false, established_outside: true,
  role: "both", uses_ai: false, subprocessors: "1-10", has_dpo: false,
  has_eu_rep: false, has_uk_rep: false, has_swiss_rep: false, scale: "SME",
  sector: "SaaS / Technology", special_category: false,
};

const delay = (ms) => new Promise((r) => setTimeout(r, ms));

export default function App() {
  const [config, setConfig] = useState(null);
  const [enforcementCases, setEnforcementCases] = useState([]);
  const [stage, setStage] = useState("idle"); // idle | analysing | assessment
  const [company, setCompany] = useState("your company");
  const [domain, setDomain] = useState(null);
  const [assumptions, setAssumptions] = useState(DEFAULT_ASSUMPTIONS);
  const [baseAssumptions, setBaseAssumptions] = useState(DEFAULT_ASSUMPTIONS);
  const [fieldConfidence, setFieldConfidence] = useState({});
  const [graphFacts, setGraphFacts] = useState(null);
  const [sourceKind, setSourceKind] = useState("graph");
  const [revision, setRevision] = useState(0);
  const [correctedKeys, setCorrectedKeys] = useState(() => new Set());
  const [chatOpen, setChatOpen] = useState(true);
  const [copilotTalking, setCopilotTalking] = useState(false);
  const [railTalking, setRailTalking] = useState(false);
  const [error, setError] = useState(null);
  const [seedUrl, setSeedUrl] = useState("");
  const [searchedUrl, setSearchedUrl] = useState("");
  const sessionId = useMemo(() => (crypto.randomUUID ? crypto.randomUUID() : String(Date.now())), []);

  useEffect(() => {
    axios.get(`${API}/config`).then((r) => setConfig(r.data)).catch(() => setError("Could not load configuration."));
    axios.get(`${API}/enforcement`).then((r) => setEnforcementCases(r.data.cases || [])).catch(() => {});
  }, []);

  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const urlParam = p.get("url") || p.get("domain");
    if (urlParam) {
      setSeedUrl(urlParam);
      if (p.get("view") === "results") runProfile(urlParam);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fromPrice = useMemo(() => {
    if (!config) return "\u00a399/month";
    const reps = config.modules.filter((m) => m.category === "representative" && m.priceModel === "monthly");
    const min = Math.min(...reps.map((m) => m.bands.Micro));
    return `${fmtMoney(min, "GBP", config.currencies)}/month`;
  }, [config]);

  const applyProfile = (data) => {
    setCompany(data.company || "your company");
    setDomain(data.domain || null);
    const a = { ...DEFAULT_ASSUMPTIONS, ...(data.assumptions || {}) };
    setAssumptions(a);
    setBaseAssumptions(a);
    setFieldConfidence(data.field_confidence || {});
    setGraphFacts(data.facts || null);
    setSourceKind(data.source_kind || "generic");
  };

  const nameFromUrl = (raw) => {
    const label = String(raw || "").replace(/^https?:\/\//i, "").replace(/^www\./i, "").split(/[/:?#]/)[0].split(".")[0];
    if (!label) return "your company";
    return label.replace(/[-_]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  };
  const cleanDomain = (raw) => String(raw || "").replace(/^https?:\/\//i, "").replace(/^www\./i, "").split(/[/:?#]/)[0];

  const runProfile = async (rawUrl, { warm = false, refresh = false } = {}) => {
    setError(null);
    setSearchedUrl(cleanDomain(rawUrl));
    setCorrectedKeys(new Set());
    if (!warm) setStage("analysing");
    const started = Date.now();
    let data = null, ok = false, rate = false;
    while (Date.now() - started < 180000) {
      try {
        const resp = await axios.get(`${API}/profile`, { params: { url: rawUrl, refresh: refresh ? 1 : 0 } });
        data = resp.data;
        if (data && data.source_kind === "graph") { ok = true; break; }
      } catch (e) {
        if (e?.response?.data?.error === "rate_limited") { rate = true; break; }
        if (e?.response?.status === 400) break;
      }
      await delay(5000);
    }
    if (ok && data) {
      const elapsed = Date.now() - started;
      if (!warm && elapsed < 1200) await delay(1200 - elapsed);
      applyProfile(data);
    } else if (data) {
      applyProfile(data);
      setError("This took longer than expected, so the findings below are general starting points. Tell the copilot about your business and I will refine them.");
    } else {
      setCompany(nameFromUrl(rawUrl)); setDomain(null); setAssumptions(DEFAULT_ASSUMPTIONS); setBaseAssumptions(DEFAULT_ASSUMPTIONS); setFieldConfidence({}); setGraphFacts(null); setSourceKind("generic");
      setError(rate ? "Too many requests. Please wait a moment and try again." : "I could not retrieve this website's public records. The findings below are general starting points.");
    }
    setStage("assessment");
    setChatOpen(true);
    // Reflect the results state in the address bar so browser history captures
    // it. Returning here (for example via back) restores these findings rather
    // than a fresh restart.
    const rp = cleanDomain(rawUrl);
    if (rp) {
      const nextUrl = `${window.location.pathname}?url=${encodeURIComponent(rp)}&view=results`;
      window.history.replaceState({ view: "results", url: rp }, "", nextUrl);
    }
  };

  const changeAssumption = (key, value) => {
    if (domain) axios.post(`${API}/correction`, { domain, changes: { [key]: value } }).catch(() => {});
    setAssumptions((prev) => ({ ...prev, [key]: value }));
    setCorrectedKeys((prev) => new Set(prev).add(key));
    setRevision((r) => r + 1);
  };

  const undoCorrection = (keys) => {
    if (!keys || !keys.length) return;
    setAssumptions((prev) => { const n = { ...prev }; keys.forEach((k) => { n[k] = baseAssumptions[k]; }); return n; });
    setCorrectedKeys((prev) => { const n = new Set(prev); keys.forEach((k) => n.delete(k)); return n; });
    setRevision((r) => r + 1);
  };

  const applyActions = (action) => {
    if (!action) return;
    if (action.set && typeof action.set === "object") {
      setAssumptions((prev) => ({ ...prev, ...action.set }));
      setCorrectedKeys((prev) => { const n = new Set(prev); Object.keys(action.set).forEach((k) => n.add(k)); return n; });
      setRevision((r) => r + 1);
      if (stage === "idle" || stage === "analysing") setStage("assessment");
    }
  };

  const scope = useMemo(() => {
    if (!config) return {};
    const coverage = computeCoverage(assumptions);
    return { domain, company, assumptions, facts: graphFacts, modules: actionableIds(coverage), band: assumptions.scale, total: `Plans start at ${fromPrice}, exact pricing in the portal` };
  }, [config, assumptions, domain, company, fromPrice, graphFacts]);

  const registerUrl = useMemo(() => {
    const base = config?.portalRegisterUrl || "https://portal.gdprlocal.com/Account/Register";
    const params = new URLSearchParams({ src: "rep-start" });
    if (domain) params.set("ref", domain);
    const ids = actionableIds(computeCoverage(assumptions));
    if (ids.length) params.set("coverage", ids.join(","));
    if (assumptions.scale) params.set("band", assumptions.scale);
    return `${base}?${params.toString()}`;
  }, [config, domain, assumptions]);

  const signup = () => { window.location.href = registerUrl; };

  if (!config) {
    return <div className="App min-h-screen flex items-center justify-center"><div className="text-mutedink">Loading...</div></div>;
  }

  const introGreeting = `Hi, I'm about to make this 100x easier than you were expecting!\n\nFirstly, just tell me your website and I'll scan your compliance landscape and tailor this page to your exact scenario.`;

  const listJoin = (arr) => (arr.length <= 1 ? (arr[0] || "") : arr.slice(0, -1).join(", ") + " and " + arr[arr.length - 1]);
  const buildRailGreeting = () => {
    const cname = company && company !== "your company" ? company : "your business";
    if (sourceKind !== "graph") {
      return `Okay, that is my best read for ${cname} for now. I could not fully verify it from public records, so tell me a little about what you do and where your customers are, and I will sharpen it. I can also walk you through the sign up process whenever you are ready.`;
    }
    const cov = computeCoverage(assumptions);
    const req = cov.filter((c) => c.status === "required").map((c) => c.name);
    const fw = (graphFacts?.frameworks || []).filter((f) => f.applicable).map((f) => f.name);
    const fwLine = fw.length ? `You look in scope for ${listJoin(fw)}. ` : "";
    const repLine = req.length
      ? `Based on that, you need ${req.length} representative${req.length > 1 ? "s" : ""}: ${listJoin(req)}. `
      : `Good news: you do not appear to need a representative, since you seem established where your users are. `;
    return `Okay, there you go, that is the situation as we see it for ${cname}. ${fwLine}${repLine}Ask me anything about these findings, or I can walk you through the sign up process whenever you are ready.`;
  };
  const railGreeting = buildRailGreeting();
  const railSuggestions = ["Why do I need this?", "What does sign up involve?", "What will it cost?", "We already have a representative", "We are also in Switzerland"];

  const chatHeader = (mascot = null) => (
    <div className="px-4 py-3 border-b hairline flex items-center justify-between gap-2 bg-asphalt-tint">
      <div className="flex items-center gap-2">
        {mascot || <span className="w-7 h-7 rounded-full flex items-center justify-center" style={{ background: "#2FE1EC" }}><Sparkles size={14} color="#364F6B" /></span>}
        <div className="font-bold text-asphalt text-[14px]">Compliance copilot</div>
      </div>
      {stage !== "idle" && (
        <button onClick={() => setChatOpen(false)} className="text-mutedink hover:text-asphalt p-1" data-testid="chat-collapse" aria-label="Minimise chat"><X size={18} /></button>
      )}
    </div>
  );

  // Idle: a giant GDPRLocal icon acts as the copilot avatar, with the intro
  // chat sitting inside a speech bubble to its right.
  if (stage === "idle") {
    return (
      <div className="App">
        <SiteHeader />
        <main className="max-w-[900px] mx-auto px-5 py-16 lg:py-24 overflow-x-hidden" data-testid="intro-chat">
          <div className="text-center mb-10">
            <span className="inline-flex items-center gap-2 text-[13px] font-medium text-asphalt bg-white px-3 py-1.5 rounded-full border hairline"><ShieldCheck size={15} /> Indicative guidance, not legal advice</span>
            <h1 className="mt-6 text-4xl sm:text-5xl font-bold text-asphalt leading-[1.05]">Coverage starts with clarity</h1>
          </div>
          <div className="flex items-start gap-2 sm:gap-3 lg:gap-4">
            <CopilotAvatar talking={copilotTalking} className="hidden sm:block shrink-0 w-[184px] h-[184px] lg:w-[240px] lg:h-[240px] mt-1" />
            <div className="relative flex-1 min-w-0">
              <span className="speech-tail hidden sm:block" aria-hidden="true" />
              <div className="relative rounded-2xl bg-white shadow-xl overflow-hidden h-[320px] flex flex-col border-2 border-[#cdeff2]">
                {chatHeader()}
                <Copilot sessionId={sessionId} scope={scope} onUrl={(u) => runProfile(u)} onApplyActions={applyActions} onTalking={setCopilotTalking} greeting={introGreeting} suggestions={[]} initialInput={seedUrl} />
              </div>
            </div>
          </div>
        </main>
        <SiteFooter />
      </div>
    );
  }

  // After scan: split view, chat docks to a collapsible right rail.
  return (
    <div className="App">
      <SiteHeader />
      {error && (
        <div className="max-w-[1240px] mx-auto px-5 lg:px-8 pt-4">
          <div className="text-[13.5px] text-asphalt bg-aqua-tint border hairline rounded-lg px-4 py-2.5" data-testid="error-banner">{error}</div>
        </div>
      )}

      <div className="flex flex-col lg:flex-row items-start">
        <main className="flex-1 min-w-0 w-full">
          {stage === "analysing" && <AnalysingState company={company} searchedUrl={searchedUrl} />}
          {stage === "assessment" && (
            <>
              <CoverageAssessment key={domain || "none"} sessionId={sessionId} company={company} domain={domain} searchedUrl={searchedUrl} assumptions={assumptions} fieldConfidence={fieldConfidence} graphFacts={graphFacts} sourceKind={sourceKind} correctedKeys={[...correctedKeys]} fromPrice={fromPrice} registerUrl={registerUrl} enforcementCases={enforcementCases} revision={revision} onChange={changeAssumption} onUndo={undoCorrection} onSignup={signup} />
              <RepServices assumptions={assumptions} />
              <FrameworkBand assumptions={assumptions} />
              <CountersBand counters={config.counters} />
              <Testimonials />
              <ClosingCTA fromPrice={fromPrice} registerUrl={registerUrl} />
            </>
          )}
          <SiteFooter />
        </main>

        {chatOpen && stage === "assessment" && (
          <aside className="w-full lg:w-[380px] xl:w-[400px] shrink-0 lg:sticky lg:top-[68px] lg:h-[calc(100vh-68px)] border-l hairline bg-white" data-testid="chat-rail">
            <div className="flex flex-col h-full">
              {chatHeader(<CopilotAvatar talking={railTalking} greet idleNudge className="w-9 h-9 shrink-0" />)}
              <Copilot key={`rail-${domain || "x"}`} sessionId={sessionId} scope={scope} onApplyActions={applyActions} onSignup={signup} onTalking={setRailTalking} signupInGreeting greeting={railGreeting} suggestions={railSuggestions} />
            </div>
          </aside>
        )}
      </div>

      {!chatOpen && (
        <button onClick={() => setChatOpen(true)} className="fixed bottom-6 right-6 z-50 btn-aqua rounded-full px-5 py-3.5 shadow-xl inline-flex items-center gap-2" data-testid="chat-reopen">
          <MessageSquare size={18} color="#364F6B" /> Ask the copilot
        </button>
      )}
    </div>
  );
}
